import { forwardRef, type KeyboardEvent, type ChangeEvent } from "react";
import { ScanBarcode } from "lucide-react";
import clsx from "clsx";
import { normalizeScan } from "../../lib/scan";
import styles from "./ScanInput.module.css";

export interface ScanInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when the user presses Enter. Receives the current value. */
  onScan?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  /** Neutral hint text shown below the input in muted style. */
  hint?: string;
  /** Error message shown below the input in red. Takes precedence over hint. */
  error?: string;
  /** Use compact when the scanner is already inside a Card or inline row. */
  variant?: "hero" | "compact";
}

export const ScanInput = forwardRef<HTMLInputElement, ScanInputProps>(function ScanInput(
  {
    value,
    onChange,
    onScan,
    placeholder = "Naskenujte nebo zadejte kód…",
    disabled,
    className,
    inputClassName,
    hint,
    error,
    variant = "hero",
  },
  ref,
) {
  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onScan?.(normalizeScan(value));
    }
  }

  return (
    <div className={clsx(styles.scanHero, variant === "compact" && styles.compact, className)}>
      <div className={styles.scanRow}>
        <span className={styles.scanIcon} aria-hidden="true">
          <ScanBarcode size={32} strokeWidth={1.5} />
        </span>
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          className={clsx(styles.input, inputClassName)}
          aria-label={placeholder}
        />
      </div>
      {error ? <p className={styles.error}>{error}</p> : hint && <p className={styles.hint}>{hint}</p>}
    </div>
  );
});
